import type { FC } from 'react'
import type { GameId } from '@/types'
import type { SceneProps } from '@/components/landing/games/scene-ui'

import ButtonLink from '@/components/landing/button-link'
import GameTile from '@/components/landing/game-tile'
import SectionHeading, { Accent } from '@/components/landing/section-heading'
import BaccaratScene from '@/components/landing/games/baccarat-scene'
import BlackjackScene from '@/components/landing/games/blackjack-scene'
import CrashScene from '@/components/landing/games/crash-scene'
import DiamondsScene from '@/components/landing/games/diamonds-scene'
import DiceScene from '@/components/landing/games/dice-scene'
import HiloScene from '@/components/landing/games/hilo-scene'
import KenoScene from '@/components/landing/games/keno-scene'
import LimboScene from '@/components/landing/games/limbo-scene'
import MinesScene from '@/components/landing/games/mines-scene'
import PlinkoScene from '@/components/landing/games/plinko-scene'
import PumpScene from '@/components/landing/games/pump-scene'
import RouletteScene from '@/components/landing/games/roulette-scene'
import WheelScene from '@/components/landing/games/wheel-scene'
import { GAMES, LINKS } from '@/constants/kinematic'

type Tile = {
    id: GameId
    Scene: FC<SceneProps>
    className: string
    stage: string
}

const TILES: Tile[] = [
    { id: 'crash', Scene: CrashScene, className: 'md:col-span-2 lg:col-span-7 lg:row-span-2', stage: 'min-h-[400px] md:min-h-[460px] lg:min-h-[540px]' },
    { id: 'mines', Scene: MinesScene, className: 'lg:col-span-5', stage: 'min-h-[310px]' },
    { id: 'dice', Scene: DiceScene, className: 'lg:col-span-5', stage: 'min-h-[310px]' },
    { id: 'limbo', Scene: LimboScene, className: 'lg:col-span-4', stage: 'min-h-[310px]' },
    { id: 'roulette', Scene: RouletteScene, className: 'lg:col-span-4', stage: 'min-h-[310px]' },
    { id: 'keno', Scene: KenoScene, className: 'lg:col-span-4', stage: 'min-h-[310px]' },
    { id: 'plinko', Scene: PlinkoScene, className: 'lg:col-span-5', stage: 'min-h-[310px]' },
    { id: 'pump', Scene: PumpScene, className: 'lg:col-span-3', stage: 'min-h-[310px]' },
    { id: 'wheel', Scene: WheelScene, className: 'lg:col-span-4', stage: 'min-h-[310px]' },
    { id: 'diamonds', Scene: DiamondsScene, className: 'lg:col-span-6', stage: 'min-h-[270px]' },
    { id: 'hilo', Scene: HiloScene, className: 'lg:col-span-6', stage: 'min-h-[330px] lg:min-h-[270px]' },
    { id: 'baccarat', Scene: BaccaratScene, className: 'lg:col-span-6', stage: 'min-h-[290px]' },
    { id: 'blackjack', Scene: BlackjackScene, className: 'lg:col-span-6', stage: 'min-h-[290px]' }
]

const GamesSection: FC = () => {
    return (
        <section id='games' aria-labelledby='games-title' className='relative py-24 md:py-32'>
            <div className='mx-auto max-w-[1320px] px-5 md:px-8'>
                <SectionHeading
                    id='games-title'
                    index='01'
                    label='The games'
                    title={<>Every game has its own objects, its own <Accent>motion</Accent> and its own sound.</>}
                    lead='The games are our own originals, built as 3D scenes for desktop and for phones. Your players open them inside your site, beside your other providers.'
                    action={(
                        <ButtonLink href={LINKS.lobby} variant='ghost' size='md' external>
                            Open the lobby
                        </ButtonLink>
                    )}
                />

                <div className='mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-12'>
                    {TILES.map((tile, index) => (
                        <GameTile
                            key={tile.id}
                            game={GAMES[tile.id]}
                            index={index + 1}
                            total={TILES.length}
                            Scene={tile.Scene}
                            className={tile.className}
                            stageClassName={tile.stage}
                        />
                    ))}
                </div>

                <p className='mt-6 font-mono text-[11px] leading-relaxed text-steel'>
                    Largest multipliers at the default 99% return to player. The tiles are motion sketches; the games themselves are full 3D scenes.
                </p>
            </div>
        </section>
    )
}

export default GamesSection
